import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { makeSelectWhoami } from '../containers/Auth_old/selectors';
import { fetchAndStoreWxJwt } from '../utils/wxJwtToken';

/**
 * Hook that handles JWT fetching and returns readiness state.
 * For unified auth accounts: automatically fetches JWT and returns true when ready.
 * For non-unified auth accounts: returns false (no JWT needed).
 *
 * @returns {boolean} Whether JWT token is ready (or not needed)
 */
function useWxJwtReady() {
  const whoami = useSelector(makeSelectWhoami());
  const [isJwtReady, setIsJwtReady] = useState(false);

  // Stable primitives derived from whoami — avoids cycling the effect on every
  // Redux dispatch that returns a new whoami object reference.
  const accountUnifiedAuth = whoami?.account_unified_auth ?? false;
  const accountSlugOrName = whoami?.account_slug || whoami?.account_name || null;

  useEffect(() => {
    if (!accountSlugOrName && !accountUnifiedAuth) {
      setIsJwtReady(false);
      return;
    }

    // No WX JWT needed for non-unified-auth accounts — proceed immediately.
    if (!accountUnifiedAuth) {
      setIsJwtReady(true);
      return;
    }

    // If unified auth is required, fetch JWT and wait for it to be ready
    let cancelled = false;

    const fetchAndWaitForJwt = async () => {
      try {
        // Fetch JWT - this will resolve the internal promise when done
        const success = await fetchAndStoreWxJwt(whoami);
        if (!cancelled) {
          setIsJwtReady(success);
        }
      } catch {
        // On error, JWT is not ready
        if (!cancelled) {
          setIsJwtReady(false);
        }
      }
    };

    fetchAndWaitForJwt();

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
      setIsJwtReady(false);
    };
  // accountUnifiedAuth and accountSlugOrName are primitive values derived from
  // whoami — they only change when the actual account config changes, not on
  // every Redux reference update.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUnifiedAuth, accountSlugOrName]);

  return isJwtReady;
}

export default useWxJwtReady;
